import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LOCALE,
  LOCALES,
  STORAGE_KEY,
  detectLocale,
  isLocaleCode,
  messagesByLocale,
} from './index';
import type { LocaleCode, TranslationMessages } from './types';

type I18nContextValue = {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: TranslationMessages;
  locales: typeof LOCALES;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    return detectLocale();
  });

  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    if (
      typeof document !== 'undefined' &&
      !window.location.pathname.includes('/lien-paiement-telegram-stars')
    ) {
      document.documentElement.lang = next === 'de-AT' ? 'de-AT' : next;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/lien-paiement-telegram-stars')) {
      return;
    }
    document.documentElement.lang = locale === 'de-AT' ? 'de-AT' : locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: messagesByLocale[locale] || messagesByLocale[DEFAULT_LOCALE],
      locales: LOCALES,
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}

export function useLocaleFromQuery() {
  const { setLocale } = useI18n();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (lang && isLocaleCode(lang)) {
      setLocale(lang);
    }
  }, [setLocale]);
}
